import { exportChangeLogColours, setChangeLogComment } from '@components/export/export.config';
import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { ICalibrationChange } from '@models/calibrations/calibration-changelog';
import { CalibrationStatus } from '@models/record-status';
import { ForecastMethods } from '@models/stable-balance/forecast-settings';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { getAcquisitionNamesByIds } from '@shared/get-acquisition-names-by-ids';
import { borders, colours, DASH, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { changeLogColours, getCalibrationRecordStatusBorderColor, getChangeLogSummary, getDateForDisplay } from '../helpers';

const POLYNOMIAL_HEADERS = ['Poly-1st', 'Poly-2nd', 'Poly-3rd', 'Poly-4th', 'Poly-5th', 'Poly-6th'];

export const addStableBalanceChangeLogToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibrations: IStableBalanceCalibration[],
  acquisitions: IAcquisition[],
  // tslint:disable-next-line: ban-types
  getStableBalanceChangelogDiff: Function
): number => {
  rowIndex = createHeaders(sheet, rowIndex, startCol);
  [...calibrations].forEach((calibration, levelOneIndex) => {
    if (levelOneIndex === calibrations.length - 1) {
      rowIndex = createLevelOneCalibration(sheet, rowIndex, startCol, calibration, acquisitions);
    } else {
      rowIndex = createLevelOneCalibration(
        sheet,
        rowIndex,
        startCol,
        calibration,
        acquisitions,
        getStableBalanceChangelogDiff(calibration, calibrations[levelOneIndex + 1])
      );
    }

    // Fix 7641: The last inactive record should not be expanded in the export
    if (calibration.RecordStatus !== CalibrationStatus.Inactive) {
      calibration.ChangeLogs.forEach((changeLog, ind) => {
        const isLastOfList = ind === calibration.ChangeLogs.length - 1;
        rowIndex = createLevelTwoCalibration(sheet, rowIndex, startCol, changeLog, isLastOfList);
      });
    }
  });

  rowIndex++;
  return rowIndex;
};

const createHeaders = (sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number): number => {
  const takenRows = 3;
  let takenCols = 0;
  let rowSpan: number;
  let colSpan: number;
  let lvl2StartCol: number;
  const startRow = rowIndex;

  rowSpan = 3;
  colSpan = 2;

  // Calibration Start
  sheet.getCell(rowIndex, startCol + takenCols).text('Calibration\nStart');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  // Calibration End
  sheet.getCell(rowIndex, startCol + takenCols).text('Calibration\nEnd');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  // Polynomial Form
  sheet.getCell(rowIndex, startCol + takenCols).text('Polynomial\nForm');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  // Selected Acquisitions
  sheet.getCell(rowIndex, startCol + takenCols).text('Selected Acquisitions');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  rowSpan = 1;

  // Historical Settings
  colSpan = 6;
  lvl2StartCol = startCol + takenCols;
  sheet.getCell(rowIndex, startCol + takenCols).text('Historical Settings');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  // Forecast Settings
  colSpan = 4;
  sheet.getCell(rowIndex, startCol + takenCols).text('Forecast Settings');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  rowIndex++;

  // Headers - Level two

  rowSpan = 2;

  // Historical Settings Level two
  // ----------------------------
  colSpan = 1;

  // Std. Dev.
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 0).text('Std. Dev.');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 0, rowSpan, colSpan);

  // Std. Dev. Mul
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 1).text('Std. Dev.\nMul');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 1, rowSpan, colSpan);

  // Damping
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 2).text('Damping');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 2, rowSpan, colSpan);

  // Haircut
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 3).text('Haircut');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 3, rowSpan, colSpan);

  // Up Damp.
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 4).text('Up Damp.');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 4, rowSpan, colSpan);

  // Down Damp.
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 5).text('Down Damp.');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 5, rowSpan, colSpan);

  // Forecast Settings Level two
  // ----------------------------
  colSpan = 1;

  // Method
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 6).text('Method');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 6, rowSpan, colSpan);

  // Growth
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 7).text('Growth');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 7, rowSpan, colSpan);

  // Up Damp.
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 8).text('Up Damp.');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 8, rowSpan, colSpan);

  // Down Damp.
  sheet.getCell(rowIndex, lvl2StartCol + colSpan * 9).text('Down Damp.');
  sheet.addSpan(rowIndex, lvl2StartCol + colSpan * 9, rowSpan, colSpan);

  // Styling

  const headerArea = sheet.getRange(startRow, startCol, takenRows, takenCols);
  headerArea
    .backColor(changeLogColours.HeaderColour)
    .foreColor('white')
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .wordWrap(true);
  headerArea.setBorder(borders.DARK_GREY_THIN, { inside: true });
  headerArea.setBorder(borders.BLACK_MEDIUM, { outline: true });

  rowIndex += 2;

  return rowIndex;
};

const createLevelOneCalibration = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibration: IStableBalanceCalibration,
  acquisitions: IAcquisition[],
  changedProps?: ICalibrationChange[]
) => {
  let takenCols = 0;
  let colSpan = 2;
  const rowSpan = 2;
  const takenRows = rowSpan;

  const haircut = calibration.Settings.Historical.Haircut;

  // Start Date

  sheet.getCell(rowIndex, startCol + takenCols).text(getDateForDisplay(new Date(calibration.CalibrationStartDate)));
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  // Deciding if we need to highlight Cell
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'CalibrationStartDate')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols++;

  // End Date

  sheet.getCell(rowIndex, startCol + takenCols).text(getDateForDisplay(new Date(calibration.CalibrationEndDate)));
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'CalibrationEndDate')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols++;

  // Polynomial Form
  sheet.getCell(rowIndex, startCol + takenCols).text(POLYNOMIAL_HEADERS[calibration.SelectedPolynomialForm]);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  if (!haircut && changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'SelectedPolynomialForm')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols++;

  // Selected Acquisitions

  const selectedAcquisitionNames = getAcquisitionNamesByIds(calibration.SelectedAcquisitionIds, acquisitions);
  if (selectedAcquisitionNames === 'NaN') {
    sheet.getCell(rowIndex, startCol + takenCols).text(DASH);
  } else {
    sheet.getCell(rowIndex, startCol + takenCols).text(selectedAcquisitionNames);
  }
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (!haircut && changedProps && changedProps.length > 0 && changedProps.find(change => change.property === 'Selected Acquisitions')) {
    sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
  }

  takenCols += colSpan;

  // StDev

  colSpan = 1;
  sheet.getCell(rowIndex, startCol + takenCols).text(haircut ? DASH : `${calibration.Settings.Historical.StdTerm}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (!haircut && changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'StdTerm')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // StDev Mul

  sheet.getCell(rowIndex, startCol + takenCols).text(haircut ? DASH : `${calibration.Settings.Historical.StdMultiplier}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (!haircut && changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'StdMultiplier')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Damping

  sheet.getCell(rowIndex, startCol + takenCols).text(haircut ? DASH : `${calibration.Settings.Historical.Damping}`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (!haircut && changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'Damping')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Haircut

  sheet.getCell(rowIndex, startCol + takenCols).text(`${(calibration.Settings.Historical.Haircut * 100).toFixed()}%`);
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (!haircut && changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'Haircut')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Up Damp

  if (calibration.Settings.Historical.Damping) {
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(haircut ? DASH : `${(calibration.Settings.Historical.HistoricalUpDamper * 100).toFixed()}%`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (!haircut && changedProps && changedProps.length > 0) {
      if (changedProps.find(change => change.property === 'HistoricalUpDamper')) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
  } else {
    sheet.getCell(rowIndex, startCol + takenCols).text(`-`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  }
  takenCols += colSpan;

  // Down Damp

  if (calibration.Settings.Historical.Damping) {
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(haircut ? DASH : `${Math.abs(calibration.Settings.Historical.HistoricalDownDamper * 100).toFixed()}%`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (!haircut && changedProps && changedProps.length > 0) {
      if (changedProps.find(change => change.property === 'HistoricalDownDamper')) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
  } else {
    sheet.getCell(rowIndex, startCol + takenCols).text(`-`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  }
  takenCols += colSpan;

  // Method

  const forecastMethod = ForecastMethods[calibration.Settings.Forecast.ForecastMethod];
  // @ts-ignore
  if (!isNaN(calibration.Settings.Forecast.ForecastMethod)) {
    sheet.getCell(rowIndex, startCol + takenCols).text(forecastMethod);
  } else {
    // @ts-ignore
    sheet.getCell(rowIndex, startCol + takenCols).text(calibration.Settings.Forecast.ForecastMethod);
  }
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'ForecastMethod')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Growth

  sheet
    .getCell(rowIndex, startCol + takenCols)
    .text(
      calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Growth %']
        ? `${(calibration.Settings.Forecast.ForecastGrowth * 100).toFixed()}%`
        : DASH
    );
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'ForecastGrowth')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Up Damp

  sheet
    .getCell(rowIndex, startCol + takenCols)
    .text(
      calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Haircut %']
        ? `${Math.abs(calibration.Settings.Forecast.ForecastUpDamper * 100).toFixed()}%`
        : DASH
    );
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'ForecastUpDamper')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Down Damp

  sheet
    .getCell(rowIndex, startCol + takenCols)
    .text(
      calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Haircut %']
        ? `${(Math.abs(calibration.Settings.Forecast.ForecastDownDamper) * 100).toFixed()}%`
        : DASH
    );
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  if (changedProps && changedProps.length > 0) {
    if (changedProps.find(change => change.property === 'ForecastDownDamper')) {
      sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
    }
  }
  takenCols += colSpan;

  // Styling

  const rowRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
  rowRange
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .wordWrap(true);

  rowRange.setBorder(borders.GREY_NORMAl, { top: true, bottom: true });
  rowRange.setBorder(borders.BLACK_MEDIUM, { right: true });
  // Record Status indicator
  rowRange.setBorder(getCalibrationRecordStatusBorderColor(calibration.RecordStatus), { left: true });

  rowIndex += takenRows;
  return rowIndex;
};

const createLevelTwoCalibration = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  changeLog: any,
  isLastOfList: boolean
): number => {
  let takenCols = 0;
  const rowSpan = 1;
  const takenRows = rowSpan;
  let userAreaStartCol: number;
  let commentAreaStartCol: number;
  let summaryAreaStartCol: number;
  const userAreaColSpan = 3;
  const commentAreaColSpan = 10;
  const summaryAreaColSpan = 2;

  // User Area / User info

  userAreaStartCol = startCol + takenCols;

  const date = new Date(changeLog.DateTime.replace('T', 'Z')).toString().substr(0, 21);
  sheet
    .getCell(rowIndex, userAreaStartCol)
    .text(`${changeLog.UserName || 'User ???'} on ${date}`)
    .foreColor(exportChangeLogColours.levelTwo.text)
    .font(`${FONT_SIZE}pt ${FONT_NAME}`);
  sheet.addSpan(rowIndex, userAreaStartCol, rowSpan, userAreaColSpan);
  takenCols += userAreaColSpan;

  // Comment Area

  commentAreaStartCol = startCol + takenCols;
  setChangeLogComment(sheet.getCell(rowIndex, commentAreaStartCol), changeLog.Comment);
  sheet.addSpan(rowIndex, commentAreaStartCol, rowSpan, commentAreaColSpan);
  takenCols += commentAreaColSpan;

  // Summary Area

  summaryAreaStartCol = startCol + takenCols;
  sheet
    .getCell(rowIndex, summaryAreaStartCol)
    .text(getChangeLogSummary(changeLog.Summary))
    .foreColor(exportChangeLogColours.levelTwo.summary)
    .font(`${FONT_SIZE}pt ${FONT_NAME}`);
  sheet.addSpan(rowIndex, summaryAreaStartCol, rowSpan, summaryAreaColSpan);
  takenCols += summaryAreaColSpan;

  // Styling

  const rowRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
  const userDataRange = sheet.getRange(rowIndex, userAreaStartCol, takenRows, userAreaColSpan);
  const commentRange = sheet.getRange(rowIndex, commentAreaStartCol, takenRows, commentAreaColSpan);
  const summaryRange = sheet.getRange(rowIndex, summaryAreaStartCol, takenRows, summaryAreaColSpan);

  rowRange
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .backColor(changeLogColours.LevelTwoColour);

  userDataRange.setBorder(borders.BLACK_THIN, { outline: true });
  commentRange.setBorder(borders.BLACK_THIN, { outline: true });
  summaryRange.setBorder(borders.BLACK_THIN, { outline: true });
  rowRange.setBorder(borders.BLACK_THIN, { all: true });
  rowRange.setBorder(borders.BLACK_MEDIUM, { left: true, right: true, bottom: isLastOfList });

  rowIndex += takenRows;

  return rowIndex;
};
